import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { BookOpen, PlayCircle, Trophy, Clock, ArrowRight, User, CheckCircle } from 'lucide-react';
import VantaBackground from '../componets/vantabackground';
import { useCourseProgress } from './useCourseProgress';
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';

const EducationDashboard = () => {
  const navigate = useNavigate();
  const { width, height } = useWindowSize();

  const handleStartCourse = () => {
    navigate('/academy/course/intro-algo');
  };

  // Fetch progress for the 'retail_quant_launchpad' course
  const { progress } = useCourseProgress('retail_quant_launchpad');
  
  // Calculate percentage (0 to 6 index, so divide by 6 for 100%)
  // Ensure we don't divide by zero if something is off, assume 6 lessons (0-6 index = 7 items)
  const totalLessons = 6; 
  const progressPercentage = Math.min(100, Math.round((progress.maxLessonReachedIndex / totalLessons) * 100));
  const isComplete = progressPercentage === 100;

  // Dynamic Stats
  const stats = [
    { 
      label: 'Courses in Progress', 
      value: isComplete ? '0' : '1', 
      icon: BookOpen, 
      color: 'text-blue-400', 
      bg: 'bg-blue-500/10' 
    },
    { 
      label: 'Hours Learned', 
      value: (progressPercentage / 100 * 2.5).toFixed(1), 
      icon: Clock, 
      color: 'text-purple-400', 
      bg: 'bg-purple-500/10' 
    },
    { 
      label: 'Certificates', 
      value: isComplete ? '1' : '0', 
      icon: Trophy, 
      color: 'text-yellow-400', 
      bg: 'bg-yellow-500/10' 
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  return (
    <div className="relative min-h-screen w-full bg-black text-white font-sans overflow-x-hidden">
      {isComplete && <Confetti width={width} height={height} recycle={false} numberOfPieces={500} />}

      {/* Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <VantaBackground
            color={0x0d47a1}
            backgroundColor={0x000000}
            scale={1.4}
            scaleMobile={1.0}
        />
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 p-4 md:p-8 pt-20 max-w-7xl mx-auto">
        
        {/* Header */}
        <motion.div 
          className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div>
            <h1 className="text-3xl md:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500 font-orbitron">
              Student Dashboard
            </h1>
            <p className="text-gray-400 mt-2">Welcome back! Ready to continue your journey?</p>
          </div>
          
          <div className="flex items-center gap-3 bg-white/10 px-4 py-2 rounded-full border border-white/10 backdrop-blur-md">
            <div className="p-2 bg-blue-500/20 rounded-full">
              <User size={20} className="text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Current Plan</p>
              <p className="text-sm font-semibold text-white">Full Access</p>
            </div>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {stats.map((stat, idx) => (
            <motion.div 
              key={idx}
              variants={itemVariants}
              className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-md hover:bg-white/10 transition-all duration-300"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-gray-400 text-sm mb-1">{stat.label}</p>
                  <h3 className="text-3xl font-bold text-white">{stat.value}</h3>
                </div>
                <div className={`p-3 rounded-xl ${stat.bg}`}>
                  <stat.icon className={`${stat.color}`} size={24} />
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Featured Course / Continue Learning */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <PlayCircle className="text-blue-500" /> 
            {isComplete ? "Completed Courses" : "Continue Learning"}
          </h2>

          <div className="group relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
            <div className="absolute inset-0 bg-blue-500/5 group-hover:bg-blue-500/10 transition-colors duration-500"></div>
            
            <div className="grid md:grid-cols-2 gap-8 p-6 md:p-8">
              <div className="space-y-6">
                <div className="space-y-2">
                  <span className="inline-block px-3 py-1 bg-blue-500/20 text-blue-400 text-xs font-bold rounded-full uppercase tracking-wider">
                    Module 1
                  </span>
                  <h3 className="text-2xl md:text-3xl font-bold text-white">
                    The Retail Quant Launchpad
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    Transform from a manual trader to a "Pre-Quant" ready for automation. Master the mindset, vocabulary, and tools.
                  </p>
                </div>

                {/* Progress Bar */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Progress</span>
                    <span className="text-white font-mono">{progressPercentage}%</span>
                  </div>
                  <div className="h-2 w-full bg-gray-700 rounded-full overflow-hidden">
                    <div 
                        className={`h-full transition-all duration-1000 ${isComplete ? 'bg-green-500' : 'bg-gradient-to-r from-blue-500 to-purple-500'}`}
                        style={{ width: `${progressPercentage}%` }}
                    ></div>
                  </div>
                </div>

                <div className="pt-4">
                  <button 
                    onClick={handleStartCourse}
                    className={`w-full md:w-auto px-8 py-4 rounded-xl font-bold flex items-center justify-center gap-2 transform transition-all hover:scale-105 active:scale-95 shadow-lg ${
                        isComplete 
                        ? 'bg-green-600 hover:bg-green-500 text-white shadow-green-500/25' 
                        : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white shadow-blue-500/25'
                    }`}
                  >
                    {isComplete ? (
                        <>
                            Review Module <CheckCircle size={20} />
                        </>
                    ) : (
                        <>
                            Start Module <ArrowRight size={20} />
                        </>
                    )}
                  </button>
                </div>
              </div>

              {/* Visual Decorative Side (Hidden on small mobile if needed, but keeping for visual impact) */}
              <div className="relative h-48 md:h-full min-h-[200px] rounded-2xl overflow-hidden bg-black/50 border border-white/5 flex items-center justify-center">
                 {/* Abstract simplified diagram as preview */}
                 <div className="relative w-full h-full p-4 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-blue-500/20 border border-blue-400/50 flex items-center justify-center animate-pulse">
                        <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                    </div>
                    <div className="absolute w-full border-t border-dashed border-white/20"></div>
                 </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Upcoming Modules Preview */}
        <motion.div 
          className="mt-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <h2 className="text-xl font-bold text-white mb-6">Upcoming Modules</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
             {[
                'Python Data Structures for Finance',
                'Backtesting Strategies with Pandas',
                'Risk Management & Position Sizing'
             ].map((title, i) => (
               <div key={i} className="p-6 rounded-xl bg-white/5 border border-white/10 opacity-75 hover:opacity-100 transition-opacity cursor-pointer">
                  <div className="flex justify-between items-start mb-4">
                     <span className="text-xs text-gray-500 font-mono">MODULE {i + 2}</span>
                     <div className="p-1 bg-white/5 rounded">🔒</div>
                  </div>
                  <h4 className="font-semibold text-lg text-gray-300">{title}</h4>
               </div>
             ))}
          </div>
        </motion.div>

      </div>
    </div>
  );
};

export default EducationDashboard;
